// Sign in, sign up and password reset, as a component rather than a page.
//
// It is a component because there are two places someone can create an account:
// the /signin page, and the last step of the wizard, where a visitor who has
// just built an agent needs one before we can save it. Those must behave
// identically -- same providers, same validation, same error wording -- and the
// way to guarantee that is for there to be one of them.
//
// Three modes rather than three components because they share every field they
// have, and because someone who mistypes their password and needs a reset should
// not lose the email address they already typed.

import { useState, type FormEvent } from "react";
import { useAuth, messageFor } from "../context/AuthContext";
import { Button, ErrorBanner, Field, Notice, TextInput } from "./ui";

export type AuthMode = "signin" | "signup" | "reset";

const HEADINGS: Record<AuthMode, { title: string; cta: string }> = {
  signin: { title: "Sign in", cta: "Sign in" },
  signup: { title: "Create your account", cta: "Create account" },
  reset: { title: "Reset your password", cta: "Send reset link" }
};

type Props = {
  initialMode: AuthMode;
  // The page heading on /signin, but a step heading inside the wizard, which
  // already has an h1 of its own. Two h1s on a page is a real accessibility
  // problem rather than a styling preference.
  headingAs?: "h1" | "h2";
  // Overrides the submit button's label. The wizard says what pressing it will
  // actually do, which is build the agent they have just spent four steps on.
  signupCta?: string;
};

export default function AuthForm({ initialMode, headingAs = "h1", signupCta }: Props) {
  const { signInWithGoogle, signInWithPassword, signUpWithPassword, resetPassword } = useAuth();

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const Heading = headingAs;
  const cta = mode === "signup" && signupCta ? signupCta : HEADINGS[mode].cta;

  function switchTo(next: AuthMode) {
    setMode(next);
    setError(null);
    setSent(false);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signin") await signInWithPassword(email, password);
      else if (mode === "signup") await signUpWithPassword(email, password, name);
      else {
        await resetPassword(email);
        setSent(true);
      }
      // No navigation on success: the auth listener in AuthProvider flips the
      // whole app over once the user object arrives, so routing from here would
      // be a second source of truth about whether someone is signed in.
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Heading className="text-lg font-semibold text-slate-900">{HEADINGS[mode].title}</Heading>

      <div className="mt-5">
        <ErrorBanner message={error} />

        {sent && (
          <div className="mb-4">
            <Notice tone="success">
              If an account exists for {email}, a reset link is on its way.
            </Notice>
          </div>
        )}

        <Button
          variant="secondary"
          className="w-full"
          onClick={() => void onGoogle()}
          busy={busy}
          type="button"
        >
          <GoogleMark />
          Continue with Google
        </Button>

        <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          or
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          {mode === "signup" && (
            <Field label="Your name" htmlFor="name">
              <TextInput
                id="name"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Rivera"
              />
            </Field>
          )}

          <Field label="Email" htmlFor="email">
            <TextInput
              id="email"
              name="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </Field>

          {mode !== "reset" && (
            <Field
              label="Password"
              htmlFor="password"
              hint={mode === "signup" ? "At least 6 characters." : undefined}
            >
              <TextInput
                id="password"
                name="password"
                type="password"
                required
                minLength={6}
                // Tells a password manager to offer a new password on sign-up
                // and the saved one on sign-in.
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}

          <Button type="submit" className="w-full" busy={busy}>
            {cta}
          </Button>
        </form>

        <div className="mt-5 space-y-2 text-center text-sm text-slate-500">
          {mode === "signin" && (
            <>
              <p>
                <button type="button" className="text-brand-700 hover:underline" onClick={() => switchTo("reset")}>
                  Forgot your password?
                </button>
              </p>
              <p>
                No account yet?{" "}
                <button type="button" className="text-brand-700 hover:underline" onClick={() => switchTo("signup")}>
                  Create one
                </button>
              </p>
            </>
          )}
          {mode !== "signin" && (
            <p>
              <button type="button" className="text-brand-700 hover:underline" onClick={() => switchTo("signin")}>
                Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </>
  );
}

function GoogleMark() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 7l7.2 5.6c4.2-3.9 6.6-9.7 6.6-16.6z" />
      <path fill="#FBBC05" d="M10.4 28.7a14.5 14.5 0 0 1 0-9.3l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.2z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.2-5.6c-2.1 1.4-4.8 2.2-8.7 2.2-6.3 0-11.7-3.7-13.6-9.1l-7.8 6.2C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}
